/**
 * ResearchModeSelector - Lens/Blueprint selector for research modes
 *
 * Allows users to select their research mode/persona:
 * - Auto-Detect (default for new users)
 * - Financial Deep Dive (for Marcus - Financial Analyst)
 * - Strategic Analysis (for Elena - Consultant)
 */

import { useState } from 'react';
import {
  Sparkles,
  TrendingUp,
  Target,
  ChevronDown,
  Check,
  BookOpen,
  FlaskConical
} from 'lucide-react';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import type { ResearchMode } from '@/types/researchModes';
import { RESEARCH_MODE_CONFIGS, isAlphaMode } from '@/types/researchModeConfigs';

interface ResearchModeSelectorProps {
  value: ResearchMode;
  onChange: (mode: ResearchMode) => void;
  disabled?: boolean;
  className?: string;
  variant?: 'pills' | 'dropdown';
}

// Icon mapping
const MODE_ICONS: Record<ResearchMode, React.ReactNode> = {
  auto: <Sparkles className="w-4 h-4" />,
  general: <BookOpen className="w-4 h-4" />,
  financial: <TrendingUp className="w-4 h-4" />,
  strategic: <Target className="w-4 h-4" />,
};

// Color mapping for active states - enhanced with stronger visual feedback
const MODE_COLORS: Record<ResearchMode, string> = {
  auto: 'bg-verdict-primary/30 border-verdict-primary/70 text-verdict-primary ring-1 ring-verdict-primary/40 shadow-[0_0_12px_rgba(245,158,11,0.25)]',
  general: 'bg-purple-500/30 border-purple-500/70 text-purple-400 ring-1 ring-purple-500/40 shadow-[0_0_12px_rgba(168,85,247,0.25)]',
  financial: 'bg-green-500/30 border-green-500/70 text-green-400 ring-1 ring-green-500/40 shadow-[0_0_12px_rgba(34,197,94,0.25)]',
  strategic: 'bg-blue-500/30 border-blue-500/70 text-blue-400 ring-1 ring-blue-500/40 shadow-[0_0_12px_rgba(59,130,246,0.25)]',
};

const MODE_HOVER_COLORS: Record<ResearchMode, string> = {
  auto: 'hover:bg-verdict-primary/10 hover:border-verdict-primary/30',
  general: 'hover:bg-purple-500/10 hover:border-purple-500/30',
  financial: 'hover:bg-green-500/10 hover:border-green-500/30',
  strategic: 'hover:bg-blue-500/10 hover:border-blue-500/30',
};

export function ResearchModeSelector({
  value,
  onChange,
  disabled = false,
  className,
  variant = 'pills',
}: ResearchModeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const modes: ResearchMode[] = ['auto', 'general', 'financial', 'strategic'];
  const currentConfig = RESEARCH_MODE_CONFIGS[value];

  if (variant === 'dropdown') {
    return (
      <div className={cn('relative', className)}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all',
            'bg-surface/80 border-border/50',
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-border-light',
            MODE_COLORS[value]
          )}
        >
          {MODE_ICONS[value]}
          <span className="text-sm font-medium">{currentConfig.shortName}</span>
          <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
        </button>

        {isOpen && !disabled && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <div className="absolute top-full left-0 mt-2 z-50 w-64 bg-surface border border-border rounded-xl shadow-xl overflow-hidden">
              {modes.map((mode) => {
                const config = RESEARCH_MODE_CONFIGS[mode];
                const isSelected = mode === value;
                const isAlpha = isAlphaMode(mode);
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => { onChange(mode); setIsOpen(false); }}
                    className={cn(
                      'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors',
                      isSelected ? MODE_COLORS[mode] : 'hover:bg-surface-light/50'
                    )}
                  >
                    <div className={cn('mt-0.5', isSelected ? '' : 'text-text-secondary')}>
                      {MODE_ICONS[mode]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={cn('text-sm font-medium', isSelected ? '' : 'text-text-primary')}>
                          {config.name}
                        </span>
                        {isAlpha && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded">
                            <FlaskConical className="w-2.5 h-2.5" />
                            Alpha
                          </span>
                        )}
                        {isSelected && <Check className="w-3.5 h-3.5" />}
                      </div>
                      <p className="text-xs text-text-muted mt-0.5 line-clamp-2">
                        {config.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // Pills variant (default)
  return (
    <div className={cn('relative flex items-center gap-1 p-1 bg-surface/50 rounded-xl border border-border', className)}>
      {modes.map((mode) => {
        const config = RESEARCH_MODE_CONFIGS[mode];
        const isSelected = mode === value;
        const isAlpha = isAlphaMode(mode);
        const tooltipContent = (
          <div>
            <h4 className="text-sm font-semibold text-text-primary mb-1">{config.name}</h4>
            <p className="text-xs text-text-secondary leading-relaxed">{config.description}</p>
            {isAlpha && (
              <p className="text-[10px] text-amber-400/70 mt-2 leading-relaxed">
                This feature is in early testing. Must be manually selected.
              </p>
            )}
          </div>
        );
        return (
          <Tooltip key={mode} content={tooltipContent} position="top" maxWidth={260}>
            <button
              type="button"
              onClick={() => !disabled && onChange(mode)}
              disabled={disabled}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border',
                disabled && 'opacity-50 cursor-not-allowed',
                isSelected
                  ? MODE_COLORS[mode]
                  : cn('border-transparent text-text-secondary', !disabled && MODE_HOVER_COLORS[mode])
              )}
            >
              {/* Show checkmark for selected mode, otherwise show mode icon */}
              {isSelected ? (
                <Check className="w-4 h-4" />
              ) : (
                MODE_ICONS[mode]
              )}
              <span className="hidden sm:inline">{config.shortName}</span>
              {/* Alpha badge for experimental modes */}
              {isAlpha && (
                <span className="hidden sm:inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded">
                  <FlaskConical className="w-2 h-2" />
                </span>
              )}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

export default ResearchModeSelector;

